import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CalendarIcon, TrashIcon, UserGroupIcon, PlusIcon, MagnifyingGlassIcon, TagIcon } from '@heroicons/react/24/outline';
import { ProjectService } from '../../services/ProjectService';
import UserService from '../../services/UserService';
import CategoryService from '../../services/CategoryService';
import { STATUS_OPTIONS } from '../../utils/statusConfig';
import toast from 'react-hot-toast';
import { formatDateForAPI, formatDateForInput, isEndDateAfterStartDate, getMaxDateString } from '../../utils/dateUtils';

const EditProjectModal = ({ isOpen, onClose, project, onProjectUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'pending',
    initialDate: '',
    finalDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCategoryForTag, setSelectedCategoryForTag] = useState('');
  const [selectedTagId, setSelectedTagId] = useState('');

  // Cargar usuarios cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadUsers();
      loadCategories();
    }
  }, [isOpen]);

  // Inicializar datos del formulario y participantes cuando cambia el proyecto
  useEffect(() => {
    if (project && isOpen) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        status: project.status || 'pending',
        initialDate: formatDateForInput(project.initialDate),
        finalDate: formatDateForInput(project.finalDate)
      });
      
      // Inicializar participantes seleccionados con los del proyecto actual
      if (project.participants && Array.isArray(project.participants) && project.participants.length > 0) {
        const participantIds = project.participants.map(p => p.id.toString());
        setSelectedParticipants(participantIds);
      } else {
        setSelectedParticipants([]);
      }

      if (project.tags && Array.isArray(project.tags) && project.tags.length > 0) {
        const formattedTags = project.tags.map(tag => ({
          id: tag.id,
          name: tag.name,
          color: tag.color || '6077D1',
          categoryId: tag.category_id || tag.categoryId || tag.category?.id,
          categoryName: tag.category?.name
        }));
        setSelectedTags(formattedTags);
      } else {
        setSelectedTags([]);
      }
    }
  }, [project, isOpen]);

  useEffect(() => {
    if (
      project &&
      isOpen &&
      categories.length > 0 &&
      selectedTags.length === 0 &&
      Array.isArray(project.projectTags) &&
      project.projectTags.length > 0
    ) {
      const mapped = [];
      categories.forEach(category => {
        category.tags?.forEach(tag => {
          if (project.projectTags.includes(tag.id) && !mapped.some(existing => existing.id === tag.id)) {
            mapped.push({
              id: tag.id,
              name: tag.name,
              color: tag.color,
              categoryId: category.id,
              categoryName: category.name
            });
          }
        });
      });
      if (mapped.length > 0) {
        setSelectedTags(mapped);
      }
    }
  }, [categories, project, isOpen, selectedTags.length]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const result = await UserService.getUsers();
      if (result.success) {
        setUsers(result.users || []);
      } else {
        toast.error('Error al cargar la lista de usuarios');
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      toast.error('Error al cargar la lista de usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

const loadCategories = async () => {
  setLoadingCategories(true);
  try {
    const result = await CategoryService.getCategories();
    if (result.success) {
      setCategories(result.categories || []);
    } else {
      toast.error('Error al cargar las etiquetas');
    }
  } catch (error) {
    console.error('Error al cargar categorías:', error);
    toast.error('Error al cargar las etiquetas');
  } finally {
    setLoadingCategories(false);
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Obtener participantes del proyecto (usuarios seleccionados)
  const projectParticipants = useMemo(() => {
    return users.filter(user => selectedParticipants.includes(user.id.toString()));
  }, [users, selectedParticipants]);

  // Obtener miembros del tablero (usuarios disponibles, excluyendo los ya seleccionados)
  const boardMembers = useMemo(() => {
    const availableUsers = users.filter(user => !selectedParticipants.includes(user.id.toString()));
    
    // Filtrar por término de búsqueda
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      return availableUsers.filter(user => 
        user.name.toLowerCase().includes(searchLower) ||
        user.last_name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
      );
    }
    
    return availableUsers;
  }, [users, selectedParticipants, searchTerm]);

  // Agregar participante al proyecto
  const handleAddParticipant = (userId) => {
    const userIdStr = userId.toString();
    if (!selectedParticipants.includes(userIdStr)) {
      setSelectedParticipants(prev => [...prev, userIdStr]);
    }
  };

  // Remover participante del proyecto
  const handleRemoveParticipant = (userId) => {
    setSelectedParticipants(prev => prev.filter(id => id !== userId.toString()));
  };

  // Obtener iniciales de un usuario
  const getUserInitials = (name, lastName) => {
    const firstInitial = name?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  // Obtener color de avatar basado en el ID del usuario
  const getAvatarColor = (userId) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500'
    ];
    return colors[userId % colors.length];
  };

  const getTagDisplayColor = (colorValue) => {
    const hex = colorValue?.startsWith('#') ? colorValue : `#${colorValue || '4B5563'}`;
    return hex;
  };

  const handleAddTag = () => {
    if (!selectedCategoryForTag) {
      toast.error('Selecciona un grupo de etiquetas');
      return;
    }

    if (!selectedTagId) {
      toast.error('Selecciona una etiqueta');
      return;
    }

    const category = categories.find(cat => cat.id.toString() === selectedCategoryForTag);
    const tag = category?.tags?.find(t => t.id.toString() === selectedTagId);

    if (!tag) {
      toast.error('Etiqueta inválida');
      return;
    }

    if (selectedTags.some(selected => selected.id === tag.id)) {
      toast.error('La etiqueta ya está agregada');
      return;
    }

    setSelectedTags(prev => [
      ...prev,
      {
        id: tag.id,
        name: tag.name,
        color: tag.color,
        categoryId: category.id,
        categoryName: category.name
      }
    ]);

    setSelectedTagId('');
  };

  const handleRemoveTag = (tagId) => {
    setSelectedTags(prev => prev.filter(tag => tag.id !== tagId));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre del proyecto es obligatorio';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es obligatoria';
    }
    
    if (!formData.initialDate) {
      newErrors.initialDate = 'La fecha de inicio es obligatoria';
    }
    
    if (!formData.finalDate) {
      newErrors.finalDate = 'La fecha de fin es obligatoria';
    }
    
    // Validar que la fecha de fin sea posterior a la fecha de inicio
    if (formData.initialDate && formData.finalDate) {
      if (!isEndDateAfterStartDate(formData.initialDate, formData.finalDate)) {
        newErrors.finalDate = 'La fecha de fin debe ser posterior a la fecha de inicio';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Convertir IDs de participantes a números
      const projectParticipants = selectedParticipants.length > 0
        ? selectedParticipants.map(id => parseInt(id, 10))
        : [];
      
      const projectTags = selectedTags.length > 0 ? selectedTags.map(tag => tag.id) : [];

      const projectData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: formData.status,
        initialDate: formatDateForAPI(formData.initialDate),
        finalDate: formatDateForAPI(formData.finalDate),
        projectParticipants: projectParticipants,
        projectTags
      };
      
      const result = await ProjectService.updateProject(project.id, projectData);
      
      if (result.success) {
        toast.success('Proyecto actualizado exitosamente');
        if (onProjectUpdated) {
          onProjectUpdated();
        }
        handleClose();
      } else {
        toast.error(result.error || 'Error al actualizar el proyecto');
      }
    } catch (error) {
      toast.error('Error al actualizar el proyecto');
      console.error('Error updating project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      status: 'pending',
      initialDate: '',
      finalDate: ''
    });
    setErrors({});
    setSelectedParticipants([]);
    setShowParticipantsModal(false);
    setSearchTerm('');
    setSelectedTags([]);
    setSelectedCategoryForTag('');
    setSelectedTagId('');
    onClose();
  };

  const handleCloseParticipantsModal = () => {
    setShowParticipantsModal(false);
    setSearchTerm('');
  };

  const handleDelete = async () => {
    // Confirmar eliminación
    const confirmed = window.confirm(`¿Estás seguro de que deseas eliminar el proyecto "${project?.name}"?\n\nEsta acción no se puede deshacer y eliminará todas las tareas asociadas.`);
    if (!confirmed) return;

    const loadingToast = toast.loading('Eliminando proyecto...');
    setLoading(true);
    
    try {
      const result = await ProjectService.deleteProject(project.id);
      
      if (result.success) {
        toast.success('Proyecto eliminado exitosamente', { id: loadingToast });
        if (onProjectUpdated) {
          onProjectUpdated();
        }
        handleClose();
      } else {
        toast.error(result.error || 'Error al eliminar el proyecto', { id: loadingToast });
      }
    } catch (error) {
      toast.error('Error al eliminar el proyecto', { id: loadingToast });
      console.error('Error deleting project:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={handleClose}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Editar Proyecto
                </h2>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Nombre del Proyecto */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nombre del Proyecto *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`input-field ${errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Ej: Apis Login y Onboarding"
                    required
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>
                
                {/* Descripción */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descripción *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className={`input-field ${errors.description ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Describe el propósito y objetivos del proyecto"
                    required
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                </div>

                {/* Estado */}
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Estado *
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="input-field"
                    required
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Participantes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <div className="flex items-center space-x-2">
                      <UserGroupIcon className="h-4 w-4" />
                      <span>Participantes</span>
                    </div>
                  </label>
                  <div className="flex items-center flex-wrap gap-2 min-h-[40px] p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    {projectParticipants.map(user => (
                      <div
                        key={user.id}
                        className="flex items-center space-x-1 px-2 py-1 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 group"
                      >
                        <div className={`w-6 h-6 rounded-full ${getAvatarColor(user.id)} flex items-center justify-center text-white text-xs font-semibold`}>
                          {getUserInitials(user.name, user.last_name)}
                        </div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {user.name} {user.last_name}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveParticipant(user.id);
                          }}
                          className="ml-1 p-0.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 transition-all opacity-60 hover:opacity-100"
                          title="Remover participante"
                        >
                          <XMarkIcon className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setShowParticipantsModal(true)}
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                      title="Agregar participante"
                    >
                      <PlusIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>
                  {projectParticipants.length === 0 && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Haz clic en el botón + para agregar participantes
                    </p>
                  )}
                </div>
                
                {/* Etiquetas */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <div className="flex items-center space-x-2">
                      <TagIcon className="h-4 w-4" />
                      <span>Etiquetas del proyecto</span>
                    </div>
                  </label>

                  <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    {selectedTags.length > 0 ? (
                      selectedTags.map(tag => {
                        const color = getTagDisplayColor(tag.color);
                        return (
                          <div
                            key={tag.id}
                            className="flex items-center space-x-2 px-2 py-1 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                            style={{ borderColor: `${color}60` }}
                          >
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-200">
                              {tag.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tag.id)}
                              className="p-0.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                              title="Remover etiqueta"
                            >
                              <XMarkIcon className="h-3 w-3 text-red-500" />
                            </button>
                          </div>
                        );
                      })
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Aún no has agregado etiquetas
                      </span>
                    )}
                  </div>

                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                        Grupo
                      </label>
                      <select
                        value={selectedCategoryForTag}
                        onChange={(e) => {
                          setSelectedCategoryForTag(e.target.value);
                          setSelectedTagId('');
                        }}
                        disabled={loadingCategories || categories.length === 0}
                        className="input-field"
                      >
                        <option value="">Selecciona un grupo</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                        Etiqueta
                      </label>
                      <select
                        value={selectedTagId}
                        onChange={(e) => setSelectedTagId(e.target.value)}
                        disabled={
                          !selectedCategoryForTag ||
                          loadingCategories ||
                          (categories.find(c => c.id.toString() === selectedCategoryForTag)?.tags?.length || 0) === 0
                        }
                        className="input-field"
                      >
                        <option value="">Selecciona etiqueta</option>
                        {selectedCategoryForTag &&
                          categories
                            .find(category => category.id.toString() === selectedCategoryForTag)
                            ?.tags?.map(tag => (
                              <option key={tag.id} value={tag.id}>
                                {tag.name}
                              </option>
                            ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddTag}
                        disabled={
                          loadingCategories ||
                          categories.length === 0 ||
                          !selectedCategoryForTag ||
                          !selectedTagId
                        }
                        className="btn-secondary w-full"
                      >
                        Agregar etiqueta
                      </button>
                    </div>
                  </div>
                  {loadingCategories && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Cargando grupos de etiquetas...
                    </p>
                  )}
                </div>
                
                {/* Fecha de Inicio */}
                <div>
                  <label htmlFor="initialDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha de Inicio *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      id="initialDate"
                      name="initialDate"
                      value={formData.initialDate}
                      onChange={handleChange}
                      max={getMaxDateString()}
                      className={`input-field pr-10 ${errors.initialDate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                      required
                    />
                    <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                  {errors.initialDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.initialDate}</p>
                  )}
                </div>
                
                {/* Fecha de Fin */}
                <div>
                  <label htmlFor="finalDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha de Fin *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      id="finalDate"
                      name="finalDate"
                      value={formData.finalDate}
                      onChange={handleChange}
                      min={formData.initialDate}
                      max={getMaxDateString()}
                      className={`input-field pr-10 ${errors.finalDate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                      required
                    />
                    <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                  {errors.finalDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.finalDate}</p>
                  )}
                </div>
                
                {/* Botones */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  {/* Botón de eliminar a la izquierda */}
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span>Eliminar</span>
                  </button>
                  
                  {/* Botones de cancelar y actualizar a la derecha */}
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="btn-secondary"
                      disabled={loading}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={loading}
                    >
                      {loading ? 'Actualizando...' : 'Actualizar Proyecto'}
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>

    {/* Modal de Selección de Participantes */}
    <AnimatePresence>
      {showParticipantsModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50"
                onClick={handleCloseParticipantsModal}
              />
              
              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Agregar Participantes
                  </h3>
                  <button
                    onClick={handleCloseParticipantsModal}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                {/* Búsqueda */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar miembros"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input-field w-full pl-10"
                    />
                  </div>
                </div>

                {/* Contenido */}
                <div className="p-4 max-h-[400px] overflow-y-auto">
                  {/* Participantes del Proyecto */}
                  {projectParticipants.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        Participantes del proyecto
                      </h4>
                      <div className="space-y-2">
                        {projectParticipants.map(user => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`w-8 h-8 rounded-full ${getAvatarColor(user.id)} flex items-center justify-center text-white text-sm font-semibold`}>
                                {getUserInitials(user.name, user.last_name)}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {user.name} {user.last_name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveParticipant(user.id)}
                              className="p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                              title="Remover participante"
                            >
                              <XMarkIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Miembros del Tablero */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Miembros del tablero
                    </h4>
                    {loadingUsers ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        <span className="ml-2 text-sm text-gray-500">Cargando usuarios...</span>
                      </div>
                    ) : boardMembers.length > 0 ? (
                      <div className="space-y-2">
                        {boardMembers.map(user => (
                          <div
                            key={user.id}
                            onClick={() => handleAddParticipant(user.id)}
                            className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                          >
                            <div className={`w-8 h-8 rounded-full ${getAvatarColor(user.id)} flex items-center justify-center text-white text-sm font-semibold`}>
                              {getUserInitials(user.name, user.last_name)}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.name} {user.last_name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {user.email}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {searchTerm.trim() 
                            ? 'No se encontraron miembros con ese criterio' 
                            : 'No hay más miembros disponibles'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end p-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleCloseParticipantsModal}
                    className="btn-primary"
                  >
                    Aceptar
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default EditProjectModal;

